/**
 * Jepson data visibility — the frontend half of one reversible switch.
 *
 * Mirrors lib/jepsonVisibility.js on the backend. The agreement covering Jepson
 * eFlora content is under review, so SLP hides everything Jepson-sourced until
 * it is settled. Nothing is deleted anywhere; this only stops it being shown.
 *
 *     REACT_APP_JEPSON_DATA_ENABLED=true   show Jepson content again
 *     unset / anything else                hidden (the default)
 *
 * Hidden by default on purpose, same as the backend: a missing env var must fail
 * CLOSED. Note this is read at BUILD time — Create React App inlines REACT_APP_*
 * at compile — so flipping it means a rebuild, not just a restart.
 *
 * The backend already withholds these fields from every response, so the UI
 * would render them empty regardless. Hiding here too means no empty panels, no
 * orphaned "Jepson eFlora" heading, and no request for a map layer that is gone.
 */

export const JEPSON_DATA_ENABLED =
  String(process.env.REACT_APP_JEPSON_DATA_ENABLED || '').toLowerCase() === 'true';

/**
 * Columns withheld while hidden. Matches HIDDEN_PLANT_FIELDS on the backend.
 *
 * NOT here, deliberately: habitat, elevation_range, flowering_time and
 * distribution_outside_of_california. A bloom month or an elevation band is a
 * plain fact available from many sources, so the decision (2026-09-22) was to
 * keep them and only DE-LABEL — they move out of the "Jepson eFlora" grouping
 * rather than disappearing. `url` also stays: an outbound link to eFlora is fine.
 */
export const HIDDEN_PLANT_COLUMNS: string[] = [
  'jepson_description',
  'jepson_eflora_other_notes',
  'bioregional_distributions',
  'canonical_bioregions',
  'bioregion_exclusions',
  'bioregional_distributions_source',
  'jepson_derived_bioregions',
  'jepson_derived_exclusions',
  'jepson_derived_source',
  'jepson_derived_source_key_id',
  'jepson_derived_source_lead',
  'link_to_eflora_jepson',
];

/** True when a column must not be rendered. */
export const isHiddenColumn = (name?: string | null): boolean =>
  !JEPSON_DATA_ENABLED && !!name && HIDDEN_PLANT_COLUMNS.includes(name);

/** Drop hidden columns from a list of column definitions (or plain names). */
export function filterJepsonColumns(columns: any[]): any[] {
  if (JEPSON_DATA_ENABLED) return columns;
  return (columns || []).filter((c) => {
    const name = typeof c === 'string' ? c : c && (c.name || c.dataIndex || c.key);
    return !isHiddenColumn(name);
  });
}
